import { Component, Input, Output, EventEmitter, OnInit, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { FunctionalType } from "src/app/enums/functional-type";
import { WizardData } from "../modal-catalog-wizard.component";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-review-publish-step",
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: "./review-publish-step.component.html",
  styleUrls: ["./review-publish-step.component.css"],
})
export class ReviewPublishStepComponent implements OnInit {
  @Input() public isReadonly: boolean = false;
  @Input() wizardData!: WizardData;
  @Output() confirmationEvent = new EventEmitter<boolean>();
  @Output() navigateTo = new EventEmitter<number>();

  @Input() public confirmationChecked = false;

  coverFile: SafeResourceUrl | null = null;
  coverPreview: SafeResourceUrl | null = null;
  coverFileId: number | null = null;

  public readonly sanitizer = inject(DomSanitizer);
  fileBaseUrl = `${environment.api.url}/File/ViewFile`;

  requiredBenefits = 2;

  ngOnInit(): void {
    this.coverFileId = this.wizardData.multimedia?.coverFileId ?? null;
    this.coverFile = this.wizardData.multimedia.coverFile
      ? this.sanitizer.bypassSecurityTrustResourceUrl(this.wizardData.multimedia.coverFile)
      : null;
    this.coverPreview = this.wizardData.multimedia.coverPreview
      ? this.sanitizer.bypassSecurityTrustResourceUrl(this.wizardData.multimedia.coverPreview)
      : null;
  }

  isStepCompleted(step: keyof WizardData): boolean {
    return this.getStepErrors(step).length === 0;
  }

  getStepErrors(step: keyof WizardData): string[] {
    const errors: string[] = [];

    switch (step) {
      case "basicInfo": {
        const b = this.wizardData.basicInfo;
        if (!b?.title?.trim()) errors.push("Falta el título.");
        if (!b?.subtitle?.trim()) errors.push("Falta el subtítulo.");
        if (!b?.shortDescription?.trim()) errors.push("Falta la descripción corta.");
        break;
      }

      case "typePublic": {
        const t = this.wizardData.typePublic;
        const isService = t.functionalTypeId === FunctionalType.Service;
        if (isService && !t.serviceId) errors.push("Debes seleccionar un servicio.");
        if (!(t.eligibleCompany || t.eligibleCandidate || t.eligibleFreelance)) {
          errors.push("Selecciona al menos un público objetivo.");
        }
        break;
      }

      case "multimedia": {
        const m = this.wizardData.multimedia;
        if (!m?.coverFile && !m?.coverFileId) errors.push("Falta la imagen de portada.");
        break;
      }

      case "benefits": {
        const list = this.wizardData.benefits;
        const completos = list.filter(b => b.shortText?.trim() && b.iconName?.trim()).length;
        if (completos < this.requiredBenefits) {
          errors.push(`Al menos ${this.requiredBenefits} beneficios deben tener texto e icono.`);
        }
        break;
      }

      case "pricing": {
        const p = this.wizardData.pricing;
        if (!p) {
          errors.push("Falta configurar precios.");
          break;
        }

        if (!(typeof p.basePrice === "number" && p.basePrice > 0)) {
          errors.push("El precio base debe ser mayor que 0.");
        }

        if (!Array.isArray(p.plans) || p.plans.length === 0) {
          errors.push("Debes agregar al menos un plan.");
        } else {
          p.plans.forEach((pl, i) => {
            if (!pl.name?.trim()) errors.push(`Plan #${i + 1}: falta nombre.`);
            if (!pl.periodQty || pl.periodQty <= 0) errors.push(`Plan #${i + 1}: periodo inválido.`);
            if (!pl.periodUnitId) errors.push(`Plan #${i + 1}: falta unidad de periodo.`);
            if (!pl.price || pl.price <= 0) errors.push(`Plan #${i + 1}: precio inválido.`);
          });

          if (!p.plans.some(pl => pl.isActive)) {
            errors.push("Debe haber al menos un plan activo.");
          }
        }
        break;
      }

      case "capacities": {
        const c = this.wizardData.capacities;
        if (!c) {
          errors.push("Debes configurar capacidades.");
          break;
        }

        const isProduct = this.wizardData.typePublic?.functionalTypeId === FunctionalType.Product;
        const isCourse = this.wizardData.typePublic?.functionalTypeId === FunctionalType.Course;

        if (isProduct && !c.inventoryEnabled) {
          errors.push("Para productos, debes habilitar inventario.");
        }
        if (c.inventoryEnabled) {
          if (c.initialStock == null || c.initialStock < 0) {
            errors.push("Inventario: stock inicial inválido.");
          }
        }

        if (isCourse && !c.seatsEnabled) {
          errors.push("Para cursos, debes habilitar cupos.");
        }
        if (c.seatsEnabled) {
          if (!c.totalSeats || c.totalSeats <= 0) errors.push("Cupos: total inválido.");
          if (!c.holdTTLMinutes || c.holdTTLMinutes < 1) {
            errors.push("Cupos: tiempo de reserva inválido.");
          }
        }

        if (c.preApprovalRequired) {
          if (!c.paymentWindowMinutes || c.paymentWindowMinutes < 5) {
            errors.push("Aprobación: ventana de pago inválida.");
          }
          if (
            c.seatsEnabled &&
            c.holdTTLMinutes &&
            c.paymentWindowMinutes &&
            c.holdTTLMinutes >= c.paymentWindowMinutes
          ) {
            errors.push("Aprobación: el tiempo de reserva debe ser menor que la ventana de pago.");
          }
        }
        break;
      }
    }

    return errors;
  }

  get previewTitle(): string {
    return this.wizardData.basicInfo?.title || "Título pendiente";
  }

  get previewDescription(): string {
    return this.wizardData.basicInfo?.shortDescription || "Descripción corta pendiente.";
  }

  get previewBenefits(): WizardData["benefits"] {
    return Array.isArray(this.wizardData.benefits) ? this.wizardData.benefits : [];
  }

  get defaultPlan(): WizardData["pricing"]["plans"][number] | null {
    const plans = this.wizardData.pricing?.plans ?? [];
    return plans.find(p => p.isDefault) || plans.find(p => p.isActive) || null;
  }

  get basePrice(): number {
    return typeof this.wizardData.pricing?.basePrice === "number"
      ? this.wizardData.pricing.basePrice
      : 0;
  }

  get formattedPrice(): string {
    const price = this.defaultPlan?.price ?? this.basePrice ?? 0;
    return `$${Number(price).toFixed(2)}`;
  }

  get planLabel(): string {
    return this.defaultPlan?.name || "Precio base";
  }

  getAllErrors(): Record<string, string[]> {
    const sections: (keyof WizardData)[] = [
      "basicInfo", "typePublic", "multimedia", "benefits", "pricing", "capacities"
    ];
    const result: Record<string, string[]> = {};
    for (const s of sections) {
      const errs = this.getStepErrors(s);
      if (errs.length > 0) {
        result[this.mapSectionName(s)] = errs;
      }
    }
    return result;
  }

  private mapSectionName(step: keyof WizardData): string {
    switch (step) {
      case "basicInfo": return "Básicos";
      case "typePublic": return "Tipo & Público";
      case "multimedia": return "Multimedia";
      case "benefits": return "Beneficios";
      case "pricing": return "Precios/Planes";
      case "capacities": return "Capacidades";
      default: return String(step);
    }
  }

}